import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { toastr } from 'react-redux-toastr';
import { useFlags } from 'launchdarkly-react-client-sdk';
import useIntl from '../../hooks/useIntl';
import useHudVoucherPermissions from '../../hooks/useHudVoucherPermissions';
import messages from './messages';
import { normalizeTenantEventForTable } from './NewTenantEvents/hooks';
import { useManageHudVoucherTabs } from './hooks';
import useHUDVoucherDetailsQuery from './hooks.useHUDVoucherDetailsQuery';
import useHUDVoucherStatsQuery from './hooks.useHUDVoucherStatsQuery';
import useHUDVoucherTenantEventsQuery from './hooks.useHUDVoucherTenantEventsQuery';
import useMoveTenantEventToNextVoucherMutation from './hooks.useMoveTenantEventToNextVoucherMutation';
import useDownloadHUD52670Mutation from './hooks.useDownloadHUD52670Mutation';
import useSaveApprovalMutation from './hooks.useSaveApprovalMutation';
import useFinalizeApprovalMutation from './hooks.useFinalizeApprovalMutation';
import useMarkVoucherAsPaidMutation from './hooks.useMarkVoucherAsPaidMutation';
import useUndoSubmittedChangesMutation from './hooks.useUndoSubmittedChangesMutation';
import useSubmitTenantEventsToTracsMutation from './hooks.useSubmitTenantEventsToTracsMutation';
import useVoucherVarianceFlags from './hooks.useVoucherVarianceFlags';
import {
  getHudVoucherDetailsQueryKey,
  getHudVoucherMiscAccountingQueryKey,
  getRegularAssistanceQueryKey,
  getHudVoucherRepaymentAgreementsQueryKey,
  getHudVoucherSpecialClaimsQueryKey,
  getHudVoucherStatsQueryKey,
  getHudVoucherTenantEventsQueryKey,
  getHudVoucherAdjustmentsQueryKey,
} from './hooks.hudVoucherQueryKeys';
import { HUD_ASYNC_DATA_REFRESH_DURATION_MS } from './constants';
import useDownloadCertificationMutation from './hooks.useDownloadCertificationMutation';
import useRegularAssistanceQuery from './hooks.useRegularAssistanceQuery';
import useHUDAsyncDataQueryInterval from './hooks.useHUDAsyncDataQueryInterval';
import {
  updateHudVoucherRegularAssistanceApprovedAmount,
  updateHudVoucherAdjustmentApprovedAmount,
} from '../../services/hudVouchersService';
import regularAssistancePaymentsMessages from './RegularAssistancePayments/messages';
import adjustmentsMessages from './Adjustments/messages';
import {
  LOCKED_VOUCHER_SUBMISSION_STATUSES,
  VOUCHER_SUBMISSION_STATUS,
} from '../../constants/hudVoucher';

export const useManageHudVoucherDetails = ({
  match,
  organizationId,
  selectedProperty,
}) => {
  const intl = useIntl();
  const { hudTenantEvents } = useFlags();
  const { canManage, canSubmit, canApprovePayment, canPostPayments } =
    useHudVoucherPermissions();
  const hudVoucherId = match.params.voucherId;
  const propertyId = selectedProperty?.id ?? '';
  const queryClient = useQueryClient();

  const {
    data: voucher,
    isLoading: voucherLoading,
    refetch: refetchVoucherDetails,
  } = useHUDVoucherDetailsQuery({
    organizationId,
    propertyId,
    hudVoucherId,
  });

  const [submissionStatusOverride, setSubmissionStatusOverride] =
    useState(null);
  const [part6TotalOverride, setPart6TotalOverride] = useState(null);
  const [isUndoConfirmOpen, setIsUndoConfirmOpen] = useState(false);
  const [hudAsyncDataRefreshUntil, setHudAsyncDataRefreshUntil] =
    useState(null);

  useEffect(() => {
    setSubmissionStatusOverride(null);
  }, [hudVoucherId]);

  useEffect(() => {
    if (submissionStatusOverride === null && voucher?.submissionStatus) {
      setSubmissionStatusOverride(voucher.submissionStatus);
    }
  }, [voucher?.submissionStatus, submissionStatusOverride]);

  const effectiveSubmissionStatus =
    submissionStatusOverride ??
    voucher?.submissionStatus ??
    VOUCHER_SUBMISSION_STATUS.DRAFT;

  const isVoucherSubmitted =
    effectiveSubmissionStatus === VOUCHER_SUBMISSION_STATUS.SUBMITTED;
  const isVoucherApproved =
    effectiveSubmissionStatus === VOUCHER_SUBMISSION_STATUS.APPROVED;
  const isVoucherPaid =
    effectiveSubmissionStatus === VOUCHER_SUBMISSION_STATUS.PAID;
  const showPostPaymentsAndDeposits = isVoucherApproved || isVoucherPaid;
  const isVoucherLocked = LOCKED_VOUCHER_SUBMISSION_STATUSES.includes(
    effectiveSubmissionStatus,
  );
  const hasUnsubmittedEligibleEvents =
    voucher?.hasUnsubmittedEligibleEvents ?? false;
  const showSubmittedChangesWarning = Boolean(
    voucher?.hasDraftChangesSinceSubmission,
  );

  const hudAsyncDataRefetchInterval = useHUDAsyncDataQueryInterval(
    hudAsyncDataRefreshUntil,
  );

  const currentVoucher = useMemo(() => {
    const baseVoucher = {
      id: hudVoucherId ?? '',
      propertyId: match.params.propertyId ?? '',
      unitId: '',
      residentId: '',
      voucherType: '',
      tracsId: '',
      requested: 0,
      month: '',
      year: '',
      contractNumber: '',
      status: voucher?.status ?? 'Active',
      submissionStatus: effectiveSubmissionStatus,
      voucherMonthYear: voucher?.voucherMonthYear ?? '',
      voucherYearMonth: voucher?.voucherYearMonth ?? '',
    };

    const merged = { ...baseVoucher, ...(voucher ?? {}) };

    return {
      ...merged,
      tracsId: merged.tracsId ?? merged.tracsVoucherId ?? '',
      tracsVoucherId: merged.tracsVoucherId ?? merged.tracsId ?? '',
      subsidyType: merged.subsidyType ?? '',
      voucherType: merged.voucherType ?? '',
      requested: merged.totalVoucherAmount ?? 0,
      contractNumber: merged.affordableFunding?.contractNumber ?? '',
    };
  }, [
    hudVoucherId,
    match.params.propertyId,
    voucher,
    effectiveSubmissionStatus,
  ]);

  const {
    data: regularAssistanceViewModel,
    isLoading: regularAssistanceLoading,
  } = useRegularAssistanceQuery({
    organizationId,
    propertyId,
    hudVoucherId,
    voucher,
    options: {
      refetchInterval: hudAsyncDataRefetchInterval,
    },
  });

  const { data: tenantEventsData, isLoading: tenantEventsLoading } =
    useHUDVoucherTenantEventsQuery({
      organizationId,
      propertyId,
      hudVoucherId,
      hudTenantEvents,
    });

  const normalizedTenantEvents = useMemo(() => {
    const raw = tenantEventsData?.results ?? [];
    return raw.map(normalizeTenantEventForTable);
  }, [tenantEventsData]);

  const invalidateVoucherDetailQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries(
        getHudVoucherDetailsQueryKey(organizationId, propertyId, hudVoucherId),
      ),
      queryClient.invalidateQueries(
        getHudVoucherTenantEventsQueryKey(
          organizationId,
          propertyId,
          hudVoucherId,
        ),
      ),
      queryClient.invalidateQueries(
        getHudVoucherStatsQueryKey(organizationId, propertyId, hudVoucherId),
      ),
      queryClient.invalidateQueries(
        getRegularAssistanceQueryKey(organizationId, propertyId, hudVoucherId),
      ),
      queryClient.invalidateQueries(
        getHudVoucherSpecialClaimsQueryKey(
          organizationId,
          propertyId,
          hudVoucherId,
        ),
      ),
      queryClient.invalidateQueries(
        getHudVoucherMiscAccountingQueryKey(
          organizationId,
          propertyId,
          hudVoucherId,
        ),
      ),
      queryClient.invalidateQueries(
        getHudVoucherRepaymentAgreementsQueryKey(
          organizationId,
          propertyId,
          hudVoucherId,
        ),
      ),
      queryClient.invalidateQueries(
        getHudVoucherAdjustmentsQueryKey(
          organizationId,
          propertyId,
          hudVoucherId,
        ),
      ),
    ]);
  };

  const handleVoucherStatusChange = (newStatus) => {
    setSubmissionStatusOverride(newStatus);
  };

  const refreshVoucherDetails = async () => {
    if (!hudVoucherId) return;

    await queryClient.invalidateQueries(
      getHudVoucherDetailsQueryKey(organizationId, propertyId, hudVoucherId),
    );
    await refetchVoucherDetails();
  };

  const startHudAsyncDataRefresh = () => {
    setHudAsyncDataRefreshUntil(
      Date.now() + HUD_ASYNC_DATA_REFRESH_DURATION_MS,
    );
  };

  const moveToNextVoucherMutation = useMoveTenantEventToNextVoucherMutation({
    organizationId,
    propertyId,
    hudVoucherId,
    effectiveSubmissionStatus,
    intl,
    invalidateVoucherDetailQueries,
    onSubmittedVoucherReverted: () => handleVoucherStatusChange('Draft'),
  });

  const downloadHud52670Mutation = useDownloadHUD52670Mutation({
    organizationId,
    propertyId,
    hudVoucherId,
    selectedPropertyName: selectedProperty?.name,
    voucherYearMonth: currentVoucher?.voucherYearMonth,
  });

  const { data: voucherStats } = useHUDVoucherStatsQuery({
    hudVoucherId,
    organizationId,
    propertyId,
    options: {
      refetchInterval: hudAsyncDataRefetchInterval,
    },
  });

  const { hasVarianceWarning, bucketVarianceFlags } = useVoucherVarianceFlags({
    voucherStats,
    effectiveSubmissionStatus,
  });

  const saveApprovalMutation = useSaveApprovalMutation({
    organizationId,
    propertyId,
    hudVoucherId,
    invalidateVoucherDetailQueries,
  });

  const finalizeApprovalMutation = useFinalizeApprovalMutation({
    organizationId,
    propertyId,
    hudVoucherId,
    setSubmissionStatusOverride,
    invalidateVoucherDetailQueries,
  });

  const markVoucherAsPaidMutation = useMarkVoucherAsPaidMutation({
    organizationId,
    propertyId,
    hudVoucherId,
    setSubmissionStatusOverride,
    invalidateVoucherDetailQueries,
  });

  const { mutateAsync: patchRegularAssistanceApprovedAmount } = useMutation(
    ({ regularAssistancePaymentId, approvedAmount }) =>
      updateHudVoucherRegularAssistanceApprovedAmount({
        organizationId,
        propertyId,
        voucherId: hudVoucherId,
        regularAssistancePaymentId,
        approvedAmount,
      }),
    {
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries(
            getRegularAssistanceQueryKey(
              organizationId,
              propertyId,
              hudVoucherId,
            ),
          ),
          queryClient.invalidateQueries(
            getHudVoucherStatsQueryKey(
              organizationId,
              propertyId,
              hudVoucherId,
            ),
          ),
        ]);
        toastr.success(
          'Success',
          intl.formatMessage(
            regularAssistancePaymentsMessages.updateApprovedAmountSuccess,
          ),
        );
      },
      onError: (error) => {
        const msg =
          (typeof error === 'string' && error) ||
          error?.message ||
          error?.error ||
          intl.formatMessage(
            regularAssistancePaymentsMessages.updateApprovedAmountError,
          );
        toastr.error('Error', msg);
      },
    },
  );

  const onSaveRegularAssistanceApprovedAmount = useCallback(
    (row, approvedAmount) =>
      patchRegularAssistanceApprovedAmount({
        regularAssistancePaymentId: row.id,
        approvedAmount,
      }),
    [patchRegularAssistanceApprovedAmount],
  );

  const { mutateAsync: patchAdjustmentApprovedAmount } = useMutation(
    ({ adjustmentId, approvedAmount }) =>
      updateHudVoucherAdjustmentApprovedAmount({
        organizationId,
        propertyId,
        voucherId: hudVoucherId,
        adjustmentId,
        approvedAmount,
      }),
    {
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries(
            getHudVoucherAdjustmentsQueryKey(
              organizationId,
              propertyId,
              hudVoucherId,
            ),
          ),
          queryClient.invalidateQueries(
            getHudVoucherStatsQueryKey(
              organizationId,
              propertyId,
              hudVoucherId,
            ),
          ),
        ]);
        toastr.success(
          'Success',
          intl.formatMessage(adjustmentsMessages.updateApprovedAmountSuccess),
        );
      },
      onError: (error) => {
        const msg =
          (typeof error === 'string' && error) ||
          error?.message ||
          error?.error ||
          intl.formatMessage(adjustmentsMessages.updateApprovedAmountError);
        toastr.error('Error', msg);
      },
    },
  );

  const onSaveAdjustmentApprovedAmount = useCallback(
    (row, approvedAmount) =>
      patchAdjustmentApprovedAmount({
        adjustmentId: row.id,
        approvedAmount,
      }),
    [patchAdjustmentApprovedAmount],
  );

  const { tabs, tab, setTab, isVoucherEmpty } = useManageHudVoucherTabs({
    hudVoucherId,
    isVoucherSubmitted,
    organizationId,
    propertyId,
  });

  const undoSubmittedChangesMutation = useUndoSubmittedChangesMutation({
    organizationId,
    propertyId,
    hudVoucherId,
    intl,
    setSubmissionStatusOverride,
    setPart6TotalOverride,
    invalidateVoucherDetailQueries,
  });

  const handleUndoSubmittedChanges = () => setIsUndoConfirmOpen(true);

  const handleCloseUndoConfirm = () => {
    if (!undoSubmittedChangesMutation.isLoading) {
      setIsUndoConfirmOpen(false);
    }
  };

  const handleConfirmUndoSubmittedChanges = () => {
    undoSubmittedChangesMutation.mutate(undefined, {
      onSettled: () => setIsUndoConfirmOpen(false),
    });
  };

  const undoConfirmationActionsProps = [
    {
      variant: 'text',
      children: intl.formatMessage(messages.cancel),
      onClick: handleCloseUndoConfirm,
      disabled: undoSubmittedChangesMutation.isLoading,
    },
    {
      children: intl.formatMessage(messages.confirm),
      onClick: handleConfirmUndoSubmittedChanges,
      isSubmitting: undoSubmittedChangesMutation.isLoading,
      submitButton: true,
    },
  ];

  const submitTenantEventsMutation = useSubmitTenantEventsToTracsMutation({
    normalizedTenantEvents,
    effectiveSubmissionStatus,
    intl,
    invalidateVoucherDetailQueries,
    startHudAsyncDataRefresh,
    onSubmittedVoucherReverted: () => handleVoucherStatusChange('Draft'),
    voucherId: hudVoucherId,
  });

  const downloadCertificationMutation = useDownloadCertificationMutation({
    organizationId,
    propertyId,
  });

  const handleSubmitTenantEventsToTracs = (wassId, password) =>
    submitTenantEventsMutation.mutateAsync({ wassId, password });

  const reviewDetails = useMemo(() => {
    const repaymentCaHudApproved =
      part6TotalOverride ??
      Number(
        voucherStats?.repaymentAgreements?.caHudApprovedAmount ??
          voucherStats?.repaymentAgreements?.approvedAmount ??
          0,
      );

    return {
      units: {
        ...voucherStats?.reviewAndSubmitVoucher,
      },
      accounting: {
        sections: {
          regularTenantAssistancePayments: {
            requestedAmount: voucherStats?.regularAssistance?.requestedAmount,
            caHudApprovedAmount:
              voucherStats?.regularAssistance?.caHudApprovedAmount,
          },
          adjustmentsToRegularPayments: {
            requestedAmount: voucherStats?.adjustments?.requestedAmount,
            caHudApprovedAmount: voucherStats?.adjustments?.caHudApprovedAmount,
          },
          specialClaims: {
            requestedAmount: voucherStats?.specialClaims?.requestedAmount,
            caHudApprovedAmount:
              voucherStats?.specialClaims?.caHudApprovedAmount,
          },
          repaymentAgreements: {
            requestedAmount: voucherStats?.repaymentAgreements?.requestedAmount,
            caHudApprovedAmount: repaymentCaHudApproved,
          },
          miscellaneousAccounting: {
            requestedAmount:
              voucherStats?.miscellaneousAccounting?.requestedAmount,
            caHudApprovedAmount:
              voucherStats?.miscellaneousAccounting?.caHudApprovedAmount,
          },
        },
        totals: {
          requestedAmount:
            voucherStats?.reviewAndSubmitVoucher?.totalRequestedAmount,
          caHudApprovedAmount:
            voucherStats?.reviewAndSubmitVoucher?.totalCaHudApprovedAmount,
        },
      },
    };
  }, [voucherStats, part6TotalOverride]);

  return {
    bucketVarianceFlags,
    canManage,
    canSubmit,
    canApprovePayment,
    canPostPayments,
    currentVoucher,
    downloadCertificationMutation,
    effectiveSubmissionStatus,
    hasUnsubmittedEligibleEvents,
    hasVarianceWarning,
    hudTenantEvents,
    hudVoucherId,
    intl,
    isUndoConfirmOpen,
    isVoucherApproved,
    isVoucherEmpty,
    isVoucherLocked,
    isVoucherPaid,
    isVoucherSubmitted,
    normalizedTenantEvents,
    propertyId,
    reviewDetails,
    setTab,
    showPostPaymentsAndDeposits,
    showSubmittedChangesWarning,
    tab,
    tabs,
    tenantEventsLoading,
    voucherLoading,
    voucherStats,
    // mutation loading states
    isMovingToNextVoucher: moveToNextVoucherMutation.isLoading,
    isDownloading52670: downloadHud52670Mutation.isLoading,
    isSavingApproval: saveApprovalMutation.isLoading,
    isFinalizing: finalizeApprovalMutation.isLoading,
    isUndoingSubmittedChanges: undoSubmittedChangesMutation.isLoading,
    regularAssistanceLoading,
    // handlers
    handleVoucherStatusChange,
    regularAssistanceViewModel,
    refreshVoucherDetails,
    invalidateVoucherDetailQueries,
    handleUndoSubmittedChanges,
    handleCloseUndoConfirm,
    handleConfirmUndoSubmittedChanges,
    undoConfirmationActionsProps,
    handleSubmitTenantEventsToTracs,
    onMoveToNextVoucher: (tenantEventId) =>
      moveToNextVoucherMutation.mutate({ tenantEventId }),
    onDownloadHud52670: () => downloadHud52670Mutation.mutate(),
    onSaveApproval: (payload) => saveApprovalMutation.mutateAsync(payload),
    onFinalizeApproval: (payload) =>
      finalizeApprovalMutation.mutateAsync(payload),
    onMarkVoucherAsPaid: () => markVoucherAsPaidMutation.mutateAsync(),
    onSaveRegularAssistanceApprovedAmount,
    onSaveAdjustmentApprovedAmount,
    setPart6TotalOverride,
  };
};
